import { useInfiniteQuery } from '@tanstack/react-query';
import { usersApi } from '@/api/users';
import { queryKeys } from '@/lib/queryKeys';
import type { UserSummary } from '@/types/api';
import { useApiMutation, type MutationCallbacks } from '@/hooks/useApiMutation';

/** The team list, searched and paged. */
export function useUsers(search: string) {
  return useInfiniteQuery({
    queryKey: queryKeys.users.list(search),
    initialPageParam: null as string | null,
    queryFn: ({ pageParam }) => usersApi.list({ ...(search ? { search } : {}), cursor: pageParam }),
    getNextPageParam: (last) => last.nextCursor,
  });
}

const stale = () => [queryKeys.users.all];

export function useCreateUser(
  callbacks?: MutationCallbacks<UserSummary, { login: string; email: string; password: string; roles: string[] }>,
) {
  return useApiMutation(usersApi.create, stale, callbacks);
}

export function useDeleteUser(callbacks?: MutationCallbacks<{ ok: boolean }, { id: string; confirmation: string }>) {
  return useApiMutation(({ id, confirmation }) => usersApi.remove(id, confirmation), stale, callbacks);
}

export function useSetUserStatus(
  callbacks?: MutationCallbacks<{ ok: boolean }, { id: string; status: 'active' | 'disabled' }>,
) {
  return useApiMutation(({ id, status }) => usersApi.setStatus(id, status), stale, callbacks);
}

export function useSetUserRoles(callbacks?: MutationCallbacks<{ ok: boolean }, { id: string; roles: string[] }>) {
  return useApiMutation(({ id, roles }) => usersApi.setRoles(id, roles), stale, callbacks);
}

/** Unbinds a lost authenticator: the account binds a new one at its next sign-in. */
export function useResetUserMfa(callbacks?: MutationCallbacks<{ ok: boolean }, string>) {
  return useApiMutation(usersApi.resetMfa, stale, callbacks);
}
